/**
 * JobDetailSheet Component
 * Slide-out drawer showing full job details with video playback
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  Play,
  Film,
  FileText,
  BarChart3,
  Layers,
} from "lucide-react";
import { AdminVideoPlayer, VideoThumbnail } from "./AdminVideoPlayer";
import { JsonViewer } from "./JsonViewer";

// Types based on InstantDB schema
interface ProcessingJob {
  id: string;
  projectId?: string;
  status?: string;
  progress?: number;
  message?: string;
  startedAt?: number;
  completedAt?: number;
  outputR2Key?: string;
  error?: string;
  analyticsJson?: Record<string, unknown>;
  edlJson?: Record<string, unknown>;
}

interface ProjectVideo {
  id: string;
  projectId?: string;
  r2Key?: string;
  fileName?: string;
  fileSize?: number;
  duration?: number;
  uploadStatus?: string;
}

interface Project {
  id: string;
  name: string;
  userId: string;
  status?: string;
  thumbnail?: string;
}

interface JobDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: ProcessingJob | null;
  project?: Project | null;
  sourceVideos?: ProjectVideo[];
}

const statusColors: Record<string, string> = {
  queued: "bg-gray-500",
  uploading: "bg-blue-500",
  analyzing: "bg-blue-500",
  rendering: "bg-yellow-500",
  completed: "bg-green-500",
  failed: "bg-red-500",
};

const statusLabels: Record<string, string> = {
  queued: "Queued",
  uploading: "Uploading",
  analyzing: "Analyzing",
  rendering: "Rendering",
  completed: "Completed",
  failed: "Failed",
};

function formatDateTime(timestamp?: number): string {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractTranscription(
  analyticsJson?: Record<string, unknown>,
): string | null {
  if (!analyticsJson) return null;

  // Try common transcription locations in analytics data
  const analytics = analyticsJson as Record<string, unknown>;

  // Check for direct transcription field
  if (typeof analytics.transcription === "string") {
    return analytics.transcription;
  }

  // Check for transcript array
  if (Array.isArray(analytics.transcript)) {
    return analytics.transcript
      .map((t: { text?: string }) => t.text)
      .filter(Boolean)
      .join(" ");
  }

  // Check for segments with text
  if (Array.isArray(analytics.segments)) {
    return analytics.segments
      .map((s: { text?: string }) => s.text)
      .filter(Boolean)
      .join(" ");
  }

  // Check nested transcription data
  if (analytics.transcription && typeof analytics.transcription === "object") {
    const trans = analytics.transcription as Record<string, unknown>;
    if (typeof trans.text === "string") return trans.text;
    if (Array.isArray(trans.segments)) {
      return trans.segments
        .map((s: { text?: string }) => s.text)
        .filter(Boolean)
        .join(" ");
    }
  }

  return null;
}

export function JobDetailSheet({
  open,
  onOpenChange,
  job,
  project,
  sourceVideos = [],
}: JobDetailSheetProps) {
  if (!job) return null;

  const status = job.status || "queued";
  const isInProgress = ["queued", "uploading", "analyzing", "rendering"].includes(
    status,
  );
  const isFailed = status === "failed";
  const isCompleted = status === "completed";
  const transcription = extractTranscription(job.analyticsJson);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden flex flex-col">
        <SheetHeader className="shrink-0">
          <div className="flex items-center gap-3">
            <SheetTitle className="flex-1 truncate">
              {project?.name || "Unknown Project"}
            </SheetTitle>
            <Badge
              className={`${statusColors[status] || "bg-gray-500"} text-white shrink-0`}
            >
              {statusLabels[status] || status}
            </Badge>
          </div>
          <SheetDescription className="truncate">
            Job ID: {job.id}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-6 pb-6">
            {/* Progress (for in-progress jobs) */}
            {isInProgress && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{job.progress || 0}%</span>
                </div>
                <Progress value={job.progress || 0} className="h-2" />
                {job.message && (
                  <p className="text-xs text-muted-foreground">{job.message}</p>
                )}
              </div>
            )}

            {/* Error (for failed jobs) */}
            {isFailed && job.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Job Failed</AlertTitle>
                <AlertDescription className="mt-2 font-mono text-xs whitespace-pre-wrap">
                  {job.error}
                </AlertDescription>
              </Alert>
            )}

            {/* Timeline */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timeline
              </h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Started</p>
                  <p className="font-medium">
                    {formatDateTime(job.startedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Completed</p>
                  <p className="font-medium">
                    {formatDateTime(job.completedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Duration</p>
                  <p className="font-medium">
                    {job.startedAt && job.completedAt
                      ? `${Math.round((job.completedAt - job.startedAt) / 1000)}s`
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Output Video */}
            {isCompleted && job.outputR2Key && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Output Video
                </h3>
                <AdminVideoPlayer
                  r2Key={job.outputR2Key}
                  bucket="renders"
                  title="Rendered Output"
                />
              </div>
            )}

            {/* Source Videos */}
            {sourceVideos.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Film className="h-4 w-4" />
                  Source Videos ({sourceVideos.length})
                </h3>
                <div className="grid gap-3">
                  {sourceVideos.map((video) => (
                    <div
                      key={video.id}
                      className="flex gap-3 p-3 rounded-lg border bg-muted/30"
                    >
                      <VideoThumbnail className="w-24 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {video.fileName || "Untitled"}
                        </p>
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          <span>{formatDuration(video.duration)}</span>
                          <span>{formatFileSize(video.fileSize)}</span>
                          <Badge
                            variant="outline"
                            className="text-xs h-5"
                          >
                            {video.uploadStatus || "unknown"}
                          </Badge>
                        </div>
                        {video.r2Key && (
                          <p className="text-xs text-muted-foreground mt-2 font-mono truncate">
                            {video.r2Key}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Transcription */}
            {transcription && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Transcription
                </h3>
                <div className="p-3 rounded-lg border bg-muted/30">
                  <p className="text-sm whitespace-pre-wrap">{transcription}</p>
                </div>
              </div>
            )}

            {/* Analytics JSON */}
            {job.analyticsJson && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Analytics Data
                </h3>
                <JsonViewer
                  data={job.analyticsJson}
                  title="analyticsJson"
                  maxHeight="250px"
                />
              </div>
            )}

            {/* EDL JSON */}
            {job.edlJson && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Edit Decision List (EDL)
                </h3>
                <JsonViewer
                  data={job.edlJson}
                  title="edlJson"
                  maxHeight="250px"
                />
              </div>
            )}

            {/* No additional data message */}
            {!job.analyticsJson && !job.edlJson && !transcription && !isFailed && (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <Play className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No analytics or EDL data available for this job</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
